import { Card, Modal } from "imagine-ui";
import { UserRole } from "imagine-users";
import {
  Address,
  AddressInfo,
  AddressInfoCards,
  Defect,
  DefectEditor,
  DefectStatus,
  DefectType,
  DefectsApi,
  EditButton,
  NavigateBack,
  Project,
  ProjectInfo,
  ProjectsApi,
} from "punchlist";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LayoutActions, useAppDispatch } from "..";

interface Props {
  mode: "create" | "edit";
}

export const DefectEditorContainer = (props: Props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [errorMessage, setErrorMessage] = useState(
    location.state?.errorMessage || ""
  );
  const [warningMessage, setWarningMessage] = useState(
    location.state?.warningMessage || ""
  );
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || ""
  );
  const [defect, setDefect] = useState<Defect | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [address, setAddress] = useState<Address | undefined>(undefined);

  useEffect(() => {
    if (!location.state) return;
    const state = location.state;
    if (state.warningMessage) {
      setWarningMessage(state.warningMessage);
    }
    if (state.successMessage) {
      setSuccessMessage(state.successMessage);
    }
    if (state.errorMessage) {
      setErrorMessage(state.errorMessage);
    }
  }, [location]);

  const loadDefect = useCallback(async () => {
    console.log("Loading defect", params);
    if (props.mode === "edit") {
      if (!params.addressCode || !params.defectId) return;
      const addressCode = params.addressCode;
      const defectId = parseInt(params.defectId);
      console.log("Loading defect", addressCode);
      const response = await DefectsApi.getDefect(defectId);
      if (response.success && response.data) {
        setDefect(response.data);
        console.log("Loaded defect", response.data);
        return;
      } else {
        setErrorMessage(t("punchlist:defectNotFound"));
      }
    } else {
      if (!params.addressCode) return;
      const addressCode = params.addressCode;
      const response = await DefectsApi.getAddress(addressCode);
      if (response.success && response.data) {
        setAddress(response.data);
        setDefect({
          addressId: response.data.id,
          id: 0,
          title: "",
          type: DefectType.DEFAULT,
          description: "",
          userId: 0,
          userRole: UserRole.NONE,
          residentStatus: DefectStatus.OPEN,
          contractorStatus: DefectStatus.OPEN,
          managerStatus: DefectStatus.OPEN,
        });
        console.log("Loaded address", response.data);
        return;
      } else {
        setErrorMessage(t("punchlist:addressNotFound"));
      }
    }
  }, [params, props.mode]);

  const saveDefect = async (defect: Defect, images: File[]) => {
    console.log("Saving defect", defect);
    if (props.mode === "edit") {
      if (!params.addressCode || !params.defectId) return;
      setSaving(true);
      // Update existing defect
      const addressCode = params.addressCode;
      const response = await DefectsApi.updateDefect(addressCode, {
        ...defect,
        userRole: UserRole.NONE,
        userId: 0,
      });
      if (response.success) {
        const defectId = defect.id;
        // Upload images
        setUploading(true);
        const uploadResponse = await DefectsApi.uploadImages(defectId, images);
        setUploading(false);
        if (uploadResponse.errorMessage || !uploadResponse.success) {
          setWarningMessage(
            uploadResponse.errorMessage || t("punchlist:imageUploadFailed")
          );
          // Load complete defect with incl linked images
          const defectResponse = await DefectsApi.getDefect(defectId);
          if (defectResponse.success && defectResponse.data) {
            setDefect(defectResponse.data);
          }
        } else {
          // Go back to defect info page
          navigate("/" + addressCode + "/" + defectId);
        }

        console.log("Saved defect", response.data);
        setErrorMessage("");
      } else {
        setErrorMessage(t("punchlist:defectNotUpdated"));
      }
    } else if (props.mode === "create") {
      if (!params.addressCode) return;
      // Create new defect
      setSaving(true);
      const addressCode = params.addressCode;
      const response = await DefectsApi.createDefect(addressCode, {
        ...defect,
        userRole: UserRole.NONE,
        userId: 0,
      });
      if (response.success && response.data) {
        const defectId = response.data.id;
        // Upload images
        setUploading(true);
        const uploadResponse = await DefectsApi.uploadImages(defectId, images);
        setUploading(false);
        console.log("Upload response", uploadResponse);
        // Load complete defect with incl linked images
        const defectResponse = await DefectsApi.getDefect(defectId);

        if (defectResponse.success && defectResponse.data) {
          setDefect(defectResponse.data);
          // In case some images failed to upload
          if (uploadResponse.errorMessage) {
            navigate("/" + addressCode + "/" + defectId + "/edit", {
              state: {
                warningMessage: uploadResponse.errorMessage,
                successMessage: t("punchlist:defectCreated"),
              },
            });
            window.scrollTo(0, 0);
          } else {
            // Navigate to defect info page if all requests succeeded
            navigate("/" + addressCode);
          }
          console.log("Loaded defect", response.data);
        } else {
          setErrorMessage(t("punchlist:defectNotFound"));
        }
      } else {
        setErrorMessage(t("punchlist:defectNotCreated"));
      }
    }
    setSaving(false);
  };

  useEffect(() => {
    loadDefect();
  }, [loadDefect]);

  return (
    <>
      <NavigateBack
        to={"/" + params.addressCode}
        text={t("punchlist:backToOverview")}
        className="mb"
      />
      {defect && (
        <DefectEditor
          defect={defect}
          errorMessage={errorMessage}
          successMessage={successMessage}
          warningMessage={warningMessage}
          mode={props.mode}
          onSave={saveDefect}
          userRole={UserRole.NONE}
          canRemoveImages={false}
          saving={saving}
        />
      )}
      {uploading && (
        <Modal visible hideFooter hideHeader>
          <p>{t("punchlist:uploadingImages")}</p>
        </Modal>
      )}
    </>
  );
};
